import {
  CardContent,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Grid,
  Divider,
  Typography,
  CircularProgress,
  ExpansionPanelActions,
  Button,
  TextField,
} from "@material-ui/core";
import ChevronRight from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PageContainer, { CardPageItem } from "../../custom/Page";
import { getUsersAndKycs } from "../../actions/Kyc";
import { FormattedMessage } from "react-intl";
import { Roles } from "../../util";
import PropTypes from "prop-types";
import Moment from "react-moment";
import { getConfig2 } from "../../actions/Config";

const KycUsersListView = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState({ type: "", value: "" });
  const [isSumsubEnabled, setIsSumsubEnabled] = useState(false);
  const [users, setUsers] = useState({
    pendingKyc: [],
    analyzingKyc: [],
    newUsers: [],
    usersAbandonedKYC: [],
    rejectedUsers: [],
  });

  const changeInput = (e) => {
    const { id, value } = e.target;
    setSearch({ value: value, type: id });
  };

  const loadKYCs = useCallback(async () => {
    const { isSumsubKYCEnabled } = await getConfig2();
    const DISCOUNT_DAYS = 90; //todo: modificar pelo banco

    const users = await getUsersAndKycs();

    const usersAnalyzing = [];
    const usersPending = [];
    const newUsers = [];
    const usersRejected = [];

    for (const u of users) {
      if (u.docsAnalysing > 0) usersAnalyzing.push(u);
      else if (u.docsPending > 0) usersPending.push(u);
      else if (u.docsRejecteds > 0) usersRejected.push(u);
      else newUsers.push(u);
    }

    const recentUsers = [];
    const oldUsersWithoutKYC = [];
    for (const u of newUsers) {
      if (u.daysAbandoned <= DISCOUNT_DAYS) recentUsers.push(u);
      else oldUsersWithoutKYC.push(u);
    }

    const rejectedRecentUsers = [];
    const rejectedInactiveUsers = [];
    for (const u of usersRejected) {
      if (u.daysAbandoned <= DISCOUNT_DAYS) rejectedRecentUsers.push(u);
      else rejectedInactiveUsers.push(u);
    }

    const abandonedUsers = [...oldUsersWithoutKYC, ...rejectedInactiveUsers];

    usersAnalyzing.sort((a, b) => b.daysAbandoned - a.daysAbandoned);
    usersPending.sort((a, b) => b.daysAbandoned - a.daysAbandoned);
    recentUsers.sort((a, b) => b.daysAbandoned - a.daysAbandoned);
    rejectedRecentUsers.sort((a, b) => b.daysAbandoned - a.daysAbandoned);
    abandonedUsers.sort((a, b) => a.daysAbandoned - b.daysAbandoned);

    setUsers((prev) => {
      return {
        ...prev,
        analyzingKyc: usersAnalyzing,
        pendingKyc: usersPending,
        newUsers: recentUsers,
        usersAbandonedKYC: abandonedUsers,
        rejectedUsers: rejectedRecentUsers,
      };
    });
    setIsSumsubEnabled(isSumsubKYCEnabled);
  }, []);

  const getSummary = (summary) => {
    return `app.kyc.dashboard.${isSumsubEnabled ? "sumsub." : ""}${summary}`;
  };

  useEffect(() => {
    try {
      setLoading(true);
      loadKYCs();
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  }, [loadKYCs]);

  return (
    <PageContainer removeSpacing>
      <CardPageItem margin={1}>
        <CardContent>
          <Grid container spacing={2} >
            <Grid item xs={12} md={8}>
              <TextField
                id="name"
                label={<FormattedMessage id="app.name" />}
                variant="outlined"
                size="small"
                value={search.type === "name" ? search.value : ""}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={changeInput}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                id="id"
                label={<FormattedMessage id="app.search.id" />}
                variant="outlined"
                size="small"
                value={search.type === "id" ? search.value : ""}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={changeInput}
                fullWidth
              />
            </Grid>
          </Grid>
        </CardContent>
      </CardPageItem>

      {loading ? (
        <CardPageItem>
          <div style={{ display: "grid", placeItems: "center" }}>
            <CircularProgress style={{ marginTop: "25%" }} />
          </div>
        </CardPageItem>
      ) : (
        <>
          <ExpansionContainer
            summary={getSummary("analyzing")}
            data={users.analyzingKyc}
            defaultExpanded={[Roles.Kyc].includes(currentUser.role)}
            search={search}
            field={"docsAnalysing"}
            showIfDocuments={!isSumsubEnabled}
            isSumsubEnabled={isSumsubEnabled}
          />
          <ExpansionContainer summary={getSummary("pendent")} data={users.pendingKyc} search={search} field={"docsPending"} showIfDocuments={true} isSumsubEnabled={isSumsubEnabled} />
          <ExpansionContainer summary={getSummary("newUsers")} data={users.newUsers} showDaysAbandoned={false} search={search} isSumsubEnabled={isSumsubEnabled} />
          <ExpansionContainer summary={getSummary("rejecteds")} data={users.rejectedUsers} search={search} showIfDocuments={!isSumsubEnabled} isSumsubEnabled={isSumsubEnabled} />
          <ExpansionContainer summary={"app.kyc.dashboard.abandoned"} data={users.usersAbandonedKYC} search={search} showIfDocuments={true} isSumsubEnabled={isSumsubEnabled} />
        </>
      )}
    </PageContainer>
  );
};

const ExpansionContainer = ({ defaultExpanded = false, showDaysAbandoned = true, summary, data, search, showIfDocuments = false, field = "", isSumsubEnabled }) => {
  const [isClosed, setIsClosed] = useState(defaultExpanded);
  const handleChangeExpansionPanel = () => setIsClosed((prev) => !prev);

  const dataFiltered = useMemo(() => {
    const { value, type } = search;
    if (!value) return data;
    return data.filter((d) => d[type].toString().toLowerCase().includes(value.toLowerCase()));
  }, [search, data]);

  useEffect(() => {
    if (dataFiltered.length === data.length) return;
    setIsClosed(dataFiltered?.length > 0);
  }, [data, dataFiltered]);

  return (
    <CardPageItem>
      <ExpansionPanel expanded={isClosed} onChange={handleChangeExpansionPanel}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <Typography color="primary" variant="h6">
            <FormattedMessage id={summary} />
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <Grid container spacing={3}>
            {dataFiltered?.length > 0 ? (
              dataFiltered.map((user) => (
                <Grid item xs={12} key={user.id} padding={2}>
                  <Link to={`/user/${user.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                    <div
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.0)")}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "rgba(255,255,255,0.0)",
                        padding: "8px 4px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1">{`${user.name} (${user.id})`}</Typography>
                        <Typography variant="body2">
                          <FormattedMessage id="app.email" />: {user.email}
                        </Typography>
                        {field && !isSumsubEnabled && (
                          <Typography variant="body2" color="primary">
                            {user[field] + " "}
                            <FormattedMessage id={`app.document${user[field] > 1 ? "s" : ""}`} />
                          </Typography>
                        )}
                        {showIfDocuments && !user.docsAnalysing && !user.docsPending && !user.docsRejecteds && (
                          <Typography color="error" variant="body2">
                            {isSumsubEnabled ? <FormattedMessage id="app.kyc.withoutSumsub" /> : <FormattedMessage id="app.kyc.withoutDocuments" />}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2">
                          <FormattedMessage id="app.lastUpdate" />:
                        </Typography>
                        <Moment date={user.lastUpdate.split("T")[0]} format="DD/MM/YYYY" />
                        {showDaysAbandoned && user?.daysAbandoned > 0 && (
                          <Typography color="error" variant="body2">
                            <FormattedMessage id="app.kyc.dashboard.daysAbandoned" values={{ value: user.daysAbandoned }} />
                          </Typography>
                        )}
                      </Grid>
                      <ChevronRight color="action" />
                    </div>
                    <Divider />
                  </Link>
                </Grid>
              ))
            ) : search.value ? (
              <Grid item xs={12} padding={2}>
                <Typography variant="body1" align="center">
                  <FormattedMessage id="app.users.not.filter" />
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12} padding={2}>
                <Typography variant="body1" align="center">
                  <FormattedMessage id="app.kyc.dashboard.noData" />
                </Typography>
              </Grid>
            )}
          </Grid>
        </ExpansionPanelDetails>
        <ExpansionPanelActions>
          {dataFiltered?.length > 0 && (
            <Button fullWidth color="inherit" onClick={handleChangeExpansionPanel}>
              <FormattedMessage id="app.close" />
            </Button>
          )}
        </ExpansionPanelActions>
      </ExpansionPanel>
      <div style={{ marginBottom: 12 }} />
    </CardPageItem>
  );
};

KycUsersListView.propTypes = {
  currentUser: PropTypes.object.isRequired,
};
ExpansionContainer.propTypes = {
  defaultExpanded: PropTypes.bool,
  showDaysAbandoned: PropTypes.bool,
  summary: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  search: PropTypes.shape({
    value: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  showIfDocuments: PropTypes.bool,
  field: PropTypes.string,
  isSumsubEnabled: PropTypes.bool,
};
ExpansionContainer.defaultProps = {
  defaultExpanded: false,
  showDaysAbandoned: true,
  showIfDocuments: false,
  field: "",
};

function mapStateToProps(state) {
  return {
    currentUser: state.user.user.me,
  };
}

export default connect(mapStateToProps)(KycUsersListView);
